/**
 * What we do: the one login, two kinds of work. Sites and media the business
 * owns (bookings and listings), and work it does for customers (jobs and
 * orders). Switch either on later. A site owner can also print and mount its
 * own sites only — at its own rates — without taking printing for anyone else.
 */
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { capabilityLabel, useI18n, useT } from '../lib/i18n';
import { useSession } from '../lib/session';
import {
  ActionBar,
  Card,
  ErrorNote,
  Field,
  Spinner,
  SubHeader,
} from '../components/Primitives';
import { Icon } from '../components/Icon';

interface ServicesResponse {
  owner: boolean;
  capabilities: string[];
  ownSitePrintRate: number | null;
  ownSiteMountRate: number | null;
  ownSiteMedia: string[];
}

const MEDIA = [
  'ooh_hoarding',
  'ooh_society',
  'ooh_transit',
  'ooh_mobile_van',
  'digital_screen',
  'radio',
  'newspaper',
  'tv_cable',
  'cinema',
  'event_venue',
];
const WORK = [
  'print_flex',
  'print_offset',
  'print_digital',
  'signage_fabrication',
  'installation',
  'photography',
  'videography',
  'voice_studio',
  'event_management',
  'distribution',
];

export function ServicesPage() {
  const T = useT();
  const { lang } = useI18n();
  const { refresh } = useSession();
  const [data, setData] = useState<ServicesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [caps, setCaps] = useState<string[]>([]);
  const [ownOn, setOwnOn] = useState(false);
  const [printRate, setPrintRate] = useState('');
  const [mountRate, setMountRate] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const load = () =>
    api.get<ServicesResponse>('/v1/partner/services').then(
      (r) => {
        setData(r);
        setCaps(r.capabilities);
        setOwnOn(r.ownSitePrintRate !== null || r.ownSiteMountRate !== null);
        setPrintRate(r.ownSitePrintRate?.toString() ?? '');
        setMountRate(r.ownSiteMountRate?.toString() ?? '');
      },
      (e: unknown) => setError(e instanceof ApiError ? e.message : 'Error'),
    );
  useEffect(() => {
    void load();
  }, []);

  if (error) return <ErrorNote message={error} onRetry={() => void load()} />;
  if (!data) return <Spinner />;
  const ro = !data.owner;
  const hasSites = caps.some((c) => data.ownSiteMedia.includes(c));
  const toggle = (c: string) =>
    setCaps((x) => (x.includes(c) ? x.filter((y) => y !== c) : [...x, c]));
  const num = (s: string) => (s.trim() ? Number(s) : null);
  const badRate =
    ownOn &&
    hasSites &&
    ((printRate.trim() && !(Number(printRate) > 0)) ||
      (mountRate.trim() && !(Number(mountRate) > 0)) ||
      (!printRate.trim() && !mountRate.trim()));

  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api.post('/v1/partner/services', {
        capabilities: caps,
        ownSitePrintRate: ownOn && hasSites ? num(printRate) : null,
        ownSiteMountRate: ownOn && hasSites ? num(mountRate) : null,
      });
      await Promise.all([load(), refresh()]);
      setMsg({ text: T('सेव हो गया ✓', 'Saved ✓'), ok: true });
    } catch (e) {
      setMsg({
        text: e instanceof ApiError ? e.message : T('सेव नहीं हुआ।', 'Not saved.'),
        ok: false,
      });
    } finally {
      setBusy(false);
    }
  };

  const group = (title: string, sub: string, keys: string[], icon: 'listings' | 'printer') => (
    <Card className="space-y-3">
      <div className="flex items-start gap-3">
        <span className="icon-tile h-10 w-10">
          <Icon name={icon} className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-display text-lg font-bold">{title}</h2>
          <p className="text-sm text-muted">{sub}</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {keys.map((c) => {
          const on = caps.includes(c);
          return (
            <button
              key={c}
              type="button"
              disabled={ro}
              aria-pressed={on}
              onClick={() => toggle(c)}
              className={`inline-flex !min-h-[44px] items-center gap-1.5 rounded-full border px-4 text-sm font-semibold transition ${
                on
                  ? 'border-transparent text-white shadow-lift'
                  : 'border-line bg-panel text-text'
              }`}
              style={
                on
                  ? {
                      backgroundImage:
                        'linear-gradient(135deg, var(--sec-from), var(--sec-to))',
                    }
                  : undefined
              }
            >
              {on && <Icon name="check" className="h-4 w-4" strokeWidth={2.6} />}
              {capabilityLabel(c, lang)}
            </button>
          );
        })}
      </div>
    </Card>
  );

  return (
    <div className="space-y-4">
      <SubHeader
        icon="jobs"
        title={T('हम क्या करते हैं', 'What we do')}
        sub={T(
          'एक ही लॉगिन — जो काम आप करते हैं उसे चुनें। बाद में कभी भी बदल सकते हैं।',
          'One login — tick the work you do. Change it any time.',
        )}
      />
      {ro && (
        <p className="rounded-2xl bg-panel-2 p-3 text-sm text-muted">
          {T('यह सिर्फ़ मालिक बदल सकते हैं।', 'Only the business owner can change this.')}
        </p>
      )}
      {group(
        T('हमारी साइट / मीडिया', 'Sites and media we own'),
        T(
          'ग्राहक इन्हें बुक करते हैं — आपको बुकिंग मिलती है।',
          'Customers book these — you get bookings.',
        ),
        MEDIA,
        'listings',
      )}
      {group(
        T('ग्राहकों के लिए काम', 'Work we do for customers'),
        T(
          'प्रिंटिंग, लगाना, फ़ोटो… — आपको रेट भेजने के लिए काम मिलते हैं।',
          'Printing, mounting, photos… — you get jobs to quote for.',
        ),
        WORK,
        'printer',
      )}

      {hasSites && (
        <Card className="space-y-4">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              className="mt-1"
              checked={ownOn}
              disabled={ro}
              onChange={(e) => setOwnOn(e.target.checked)}
            />
            <span>
              <span className="block font-display text-lg font-bold">
                {T(
                  'अपनी साइट के लिए हम ख़ुद छापते और लगाते हैं',
                  'We print and mount for our own sites',
                )}
              </span>
              <span className="block text-sm text-muted">
                {T(
                  'आपकी साइट बुक होने पर उसका फ़्लेक्स छापना और लगाना आपको ही मिलेगा, आपके रेट पर — दूसरे प्रिंटरों को नहीं जाएगा। बाकी लोगों की प्रिंटिंग तभी मिलेगी जब ऊपर “फ़्लेक्स प्रिंटिंग” चुनें।',
                  'When one of your sites is booked, its flex printing and mounting come to you at your rates — not to other printers. You get printing for other sites only if you tick Flex printing above.',
                )}
              </span>
            </span>
          </label>
          {ownOn && (
            <div className="grid grid-cols-2 gap-3">
              <Field
                label={T('छपाई ₹ / वर्ग फ़ुट', 'Printing ₹ / sq ft')}
                hint={T('फ़्लेक्स सहित', 'incl. the flex')}
              >
                <input
                  inputMode="decimal"
                  className="tnum"
                  placeholder="12"
                  value={printRate}
                  disabled={ro}
                  onChange={(e) => setPrintRate(e.target.value.replace(/[^\d.]/g, ''))}
                />
              </Field>
              <Field
                label={T('लगाना ₹ / वर्ग फ़ुट', 'Mounting ₹ / sq ft')}
                hint={T('खाली = हम नहीं लगाते', 'blank = we don’t mount')}
              >
                <input
                  inputMode="decimal"
                  className="tnum"
                  placeholder="3"
                  value={mountRate}
                  disabled={ro}
                  onChange={(e) => setMountRate(e.target.value.replace(/[^\d.]/g, ''))}
                />
              </Field>
            </div>
          )}
          {ownOn && badRate && (
            <p className="text-sm text-bad">
              {T('कम से कम एक रेट डालें।', 'Give at least one rate (more than ₹0).')}
            </p>
          )}
        </Card>
      )}

      {msg && (
        <p className={`text-sm ${msg.ok ? 'text-good' : 'text-bad'}`} role="status">
          {msg.text}
        </p>
      )}
      {!ro && (
        <ActionBar>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || caps.length === 0 || Boolean(badRate)}
            onClick={() => void save()}
          >
            {busy ? T('सेव हो रहा है…', 'Saving…') : T('सेव करें', 'Save')}
          </button>
        </ActionBar>
      )}
    </div>
  );
}
